'use client'

import { useMemo } from 'react'
import { DefaultRoleSyncStrategy } from '@/services/auth/roleSyncStrategy'
import { useTrustedIdentityScopes } from '@/hooks/useTrustedIdentityScopes'
import type { IdentityScopes } from '@/utilities/auth/identityScopeRoles'

export type OrgDashboardAccess = {
  /** True once the active partner's feature flags have resolved. */
  isFlagReady: boolean
  /**
   * True once both the flag and (when the flag is on) org-admin role checks are resolved.
   * When the flag is off, this matches `isFlagReady` so callers can still use a single gate.
   */
  isReady: boolean
  isFlagEnabled: boolean
  isOrgAdmin: boolean
  isAshAdmin: boolean
  canViewOrgDashboard: boolean
  identityScopes: IdentityScopes | null
}

type RoleAccessState = {
  isOrgAdmin: boolean
  isAshAdmin: boolean
  isRoleCheckReady: boolean
}

const NOT_READY_ACCESS_STATE: RoleAccessState = {
  isOrgAdmin: false,
  isAshAdmin: false,
  isRoleCheckReady: false,
}

function resolveOrgDashboardRoleAccess(scopes: IdentityScopes | null | undefined): RoleAccessState {
  const roleSync = new DefaultRoleSyncStrategy()
  return {
    isOrgAdmin: roleSync.isOrgAdmin(scopes ?? undefined),
    isAshAdmin: roleSync.isAshAdmin(scopes ?? undefined),
    isRoleCheckReady: true,
  }
}

export function useOrgDashboardAccess({
  enabled = true,
  isFlagReady,
  isFlagEnabled,
}: {
  enabled?: boolean
  isFlagReady: boolean
  isFlagEnabled: boolean
}): OrgDashboardAccess {
  const resolvedFlagReady = enabled && isFlagReady

  const { scopes, isReady: areScopesReady } = useTrustedIdentityScopes({
    // Only resolve identity/roles when the flag is on — flag-off path does not need them.
    enabled: resolvedFlagReady && isFlagEnabled,
    errorMessage: 'Error checking org dashboard access:',
  })

  const roleAccess = useMemo((): RoleAccessState => {
    if (!isFlagEnabled || !resolvedFlagReady) return NOT_READY_ACCESS_STATE
    if (!areScopesReady) return NOT_READY_ACCESS_STATE
    // Fail closed: treat missing scopes as no org-dashboard access so the gate can proceed.
    if (!scopes) {
      return { isOrgAdmin: false, isAshAdmin: false, isRoleCheckReady: true }
    }
    return resolveOrgDashboardRoleAccess(scopes)
  }, [isFlagEnabled, resolvedFlagReady, areScopesReady, scopes])

  return useMemo(() => {
    // Flag off: ready as soon as partner flags resolve — no identity wait.
    // Flag on: also wait for role resolution.
    const isReady = resolvedFlagReady && (!isFlagEnabled || roleAccess.isRoleCheckReady)
    // Ash admins see the org shell for the currently selected org (one org at a time).
    const canViewOrgDashboard =
      isReady && isFlagEnabled && (roleAccess.isOrgAdmin || roleAccess.isAshAdmin)

    return {
      isFlagReady: resolvedFlagReady,
      isReady,
      isFlagEnabled,
      isOrgAdmin: roleAccess.isOrgAdmin,
      isAshAdmin: roleAccess.isAshAdmin,
      canViewOrgDashboard,
      identityScopes: isReady && isFlagEnabled ? scopes : null,
    }
  }, [resolvedFlagReady, isFlagEnabled, roleAccess, scopes])
}
